/**
 * The board.
 *
 * Two things here are the point of Phase 3.
 *
 * **The bow is drawn as a bow.** When a piece is held, every live bow is rendered as an
 * arc through the two flanking pieces with the held piece nocked on the string, and the
 * shot as an arrow down the ray to whatever it can reach. The mechanic is geometric, so
 * the explanation is geometric: you can see which two pieces are lending their support
 * and why the cell behind has to be empty. The 2023 client drew a bow shot and a one-cell
 * step identically — both a slightly lighter hex — which meant the one thing that makes
 * this game itself was invisible while playing it.
 *
 * **Pointer events, not mouse events.** The old board listened for `mousedown`,
 * `mousemove` and `mouseup` and nothing else, so on a phone it was decorative. Here a tap
 * selects and a second tap plays, or you drag; both gestures run through the same code
 * and both work with a finger, a mouse or a stylus.
 */

import { useCallback, useMemo, useRef, useState } from 'react';

import {
  allCells,
  type Board as BoardTopology,
  isKing,
  ownerOf,
  type PlayerIndex,
} from '@gok/engine';
import type { BowShot, Move } from '@gok/engine';

import { HEX_PATH, PieceTile } from './Pieces.tsx';
import { press, type Selection, type Targets } from '../game/selection.ts';

/** Cell tone by `(q - r) mod 3` — every neighbour differs, so rays read as lines. */
const TONES = ['fill-cell-a', 'fill-cell-b', 'fill-cell-c'] as const;

const toneOf = (board: BoardTopology, index: number): string => {
  const q = board.qrs[index * 3];
  const r = board.qrs[index * 3 + 1];
  return TONES[(((q - r) % 3) + 3) % 3];
};

interface Point {
  x: number;
  y: number;
}

const centre = (board: BoardTopology, index: number): Point => ({
  x: board.xy[index * 2],
  y: board.xy[index * 2 + 1],
});

export interface BoardProps {
  board: BoardTopology;
  cells: Int8Array;
  selection: Selection;
  targets: Targets;
  /** Rotate so this player's pieces are nearest the viewer. */
  viewFrom: PlayerIndex;
  /** Whose pieces may be picked up. */
  toMove: PlayerIndex;
  interactive: boolean;
  lastMove?: Move;
  threatened?: readonly number[];
  /** Cells to ring, for the tutorial's hints. */
  hint?: readonly number[];
  onSelect(selection: Selection): void;
  onPlay(move: Move): void;
}

export function Board({
  board,
  cells,
  selection,
  targets,
  viewFrom,
  toMove,
  interactive,
  lastMove,
  threatened = [],
  hint = [],
  onSelect,
  onPlay,
}: BoardProps) {
  const groupRef = useRef<SVGGElement>(null);
  /**
   * Whether the press that began this gesture already played a move.
   *
   * A tap fires `pointerdown` and `pointerup` on the same cell, and both handlers look
   * the destination up in the same `targets`. React normally re-renders in between —
   * clearing the selection, and with it the targets — so the second handler finds
   * nothing. That is a race, not a guarantee, and losing it plays the move twice. The
   * server refuses a duplicate and the tutorial ignores one, so it stayed invisible until
   * a local game against the bot, where this component is the only referee and the second
   * copy is an illegal move.
   */
  const playedThisGesture = useRef(false);
  const [hover, setHover] = useState<number | null>(null);
  const [drag, setDrag] = useState<
    { from: number; at: Point; moved: boolean } | null
  >(
    null,
  );

  // The board's true half-extent, rather than a generous guess. Cell centres reach
  // sqrt(3)*R horizontally and 1.5*R vertically; add half a hex on each axis and a hair
  // of margin. A square viewBox keeps the square container honest.
  const halfWidth = Math.sqrt(3) * (board.radius + 0.5);
  const halfHeight = 1.5 * board.radius + 1;
  const extent = Math.max(halfWidth, halfHeight) + 0.12;

  /** Board coordinates for a pointer, wherever on the page it is. */
  const pointAt = useCallback(
    (event: { clientX: number; clientY: number }): Point | null => {
      const ctm = groupRef.current?.getScreenCTM();
      if (!ctm) return null;
      const point = new DOMPoint(event.clientX, event.clientY).matrixTransform(
        ctm.inverse(),
      );
      return { x: point.x, y: point.y };
    },
    [],
  );

  /** The cell under a point. Hexes tile, so nearest centre is exact. */
  const cellAt = useCallback(
    (point: Point): number | null => {
      let best: number | null = null;
      let bestDistance = 1; // squared; beyond a cell's inradius is "off the board"
      for (let i = 0; i < board.size; i++) {
        const dx = point.x - board.xy[i * 2];
        const dy = point.y - board.xy[i * 2 + 1];
        const distance = dx * dx + dy * dy;
        if (distance < bestDistance) {
          bestDistance = distance;
          best = i;
        }
      }
      return best;
    },
    [board],
  );

  const resolve = useCallback(
    (event: { clientX: number; clientY: number }): number | null => {
      const point = pointAt(event);
      return point ? cellAt(point) : null;
    },
    [pointAt, cellAt],
  );

  const handlePointerDown = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive) return;
    playedThisGesture.current = false;

    const cell = resolve(event);
    if (cell === null) {
      onSelect(null);
      return;
    }

    const result = press(cells, toMove, targets, selection, cell);
    if (result.action === 'play') {
      playedThisGesture.current = true;
      onPlay(result.move);
      onSelect(null);
      return;
    }
    if (result.action === 'clear') {
      onSelect(null);
      return;
    }

    // The same press starts both gestures: it selects immediately, and becomes a drag
    // only once the pointer actually travels.
    event.currentTarget.setPointerCapture(event.pointerId);
    onSelect(result.selection);
    const point = pointAt(event);
    if (point) setDrag({ from: cell, at: point, moved: false });
  };

  const handlePointerMove = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive) return;
    const point = pointAt(event);
    if (!point) return;

    const cell = cellAt(point);
    setHover(cell);

    setDrag((current) => {
      if (!current) return current;
      const origin = centre(board, current.from);
      const travelled = Math.hypot(point.x - origin.x, point.y - origin.y);
      return {
        ...current,
        at: point,
        moved: current.moved || travelled > 0.35,
      };
    });
  };

  const handlePointerUp = (event: React.PointerEvent<SVGSVGElement>) => {
    if (!interactive) return;

    if (playedThisGesture.current) {
      playedThisGesture.current = false;
      setDrag(null);
      setHover(null);
      return;
    }

    const cell = resolve(event);
    const wasDragging = drag?.moved ?? false;
    setDrag(null);
    setHover(null);

    if (cell === null) {
      if (wasDragging) onSelect(null);
      return;
    }

    const move = targets.moves.get(cell);
    if (move) {
      onPlay(move);
      onSelect(null);
      return;
    }

    // A drag that ended nowhere useful puts the piece back. A tap leaves it selected,
    // so the second tap can be the destination.
    if (wasDragging) onSelect(null);
  };

  const dragged = drag?.moved ? drag : null;
  const emphasis = useMemo(() => emphasised(targets.bows, hover), [
    targets.bows,
    hover,
  ]);

  return (
    <svg
      className='h-full w-full touch-none select-none'
      viewBox={`${-extent} ${-extent} ${extent * 2} ${extent * 2}`}
      xmlns='http://www.w3.org/2000/svg'
      role='img'
      aria-label={`Game board, radius ${board.radius}`}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={handlePointerUp}
      onPointerCancel={() => {
        playedThisGesture.current = false;
        setDrag(null);
        setHover(null);
      }}
      onPointerLeave={() => setHover(null)}
    >
      {
        /*
        Rotating the whole board is a symmetry of a hex grid, so each player is shown
        their own side nearest them.

        Player 0 is the one rotated, not player 1: the formations put player 0's pieces
        at negative `r`, which is the top of the screen, so leaving the board alone would
        sit a player behind their opponent's line. A spectator sees player 0 at the
        bottom, the way white sits at the bottom of a chessboard.
      */
      }
      <g ref={groupRef} transform={viewFrom === 0 ? 'rotate(180)' : undefined}>
        {allCells(board).map((i) => (
          <path
            key={i}
            d={HEX_PATH}
            transform={`translate(${board.xy[i * 2]} ${board.xy[i * 2 + 1]})`}
            className={`${toneOf(board, i)} stroke-rule`}
            strokeWidth={0.012}
          />
        ))}

        <LastMove board={board} move={lastMove} />

        {
          /* Bows sit above the board and below the pieces, so a ray runs behind the
            piece it would capture rather than obscuring it. */
        }
        {targets.bows.map((shot) => (
          <BowGlyph
            key={shot.direction}
            board={board}
            shot={shot}
            from={selection?.kind === 'piece' ? selection.cell : -1}
            player={toMove}
            emphasised={emphasis === shot.direction}
          />
        ))}

        {hint.map((cell) => (
          <path
            key={`hint-${cell}`}
            d={HEX_PATH}
            transform={`translate(${board.xy[cell * 2]} ${
              board.xy[cell * 2 + 1]
            }) scale(0.9)`}
            className='threat-ring fill-none stroke-accent'
            strokeWidth={0.1}
            pointerEvents='none'
          />
        ))}

        {threatened.map((cell) => (
          <path
            key={`threat-${cell}`}
            d={HEX_PATH}
            transform={`translate(${board.xy[cell * 2]} ${
              board.xy[cell * 2 + 1]
            }) scale(0.93)`}
            className='threat-ring fill-none stroke-alert'
            strokeWidth={0.09}
          />
        ))}

        {allCells(board).map((i) => {
          const piece = cells[i];
          if (piece === 0) return null;
          if (dragged && i === dragged.from) return null;

          const held = selection?.kind === 'piece' && selection.cell === i;
          return (
            <g
              key={`piece-${i}`}
              transform={`translate(${board.xy[i * 2]} ${board.xy[i * 2 + 1]})`}
              className={held ? 'opacity-45' : undefined}
            >
              <PieceTile
                king={isKing(piece)}
                player={ownerOf(piece)}
                rotate={viewFrom === 0 ? 180 : 0}
              />
            </g>
          );
        })}

        {
          /* Destination markers go above the pieces: a capture has to read as a ring
            around the piece it takes, not a dot hidden underneath it. */
        }
        {[...targets.moves.keys()].map((cell) => (
          <Destination
            key={`target-${cell}`}
            at={centre(board, cell)}
            capture={targets.captures.has(cell)}
            hovered={hover === cell}
          />
        ))}

        {dragged && (
          <g
            transform={`translate(${dragged.at.x} ${dragged.at.y})`}
            opacity={0.95}
          >
            <PieceTile
              king={isKing(cells[dragged.from])}
              player={ownerOf(cells[dragged.from])}
              scale={0.9}
              rotate={viewFrom === 0 ? 180 : 0}
            />
          </g>
        )}
      </g>
    </svg>
  );
}

/** The direction of the shot whose ray the pointer is currently over. */
function emphasised(
  bows: readonly BowShot[],
  hover: number | null,
): number | null {
  if (hover === null) return null;
  for (const shot of bows) if (shot.ray.includes(hover)) return shot.direction;
  return null;
}

function LastMove({ board, move }: { board: BoardTopology; move?: Move }) {
  if (!move || (move.type !== 'movePiece' && move.type !== 'spawnPiece')) {
    return null;
  }
  return (
    <g className='fill-none stroke-accent' strokeWidth={0.07} opacity={0.5}>
      {[move.fromIndex, move.toIndex].map((cell) => (
        <path
          key={cell}
          d={HEX_PATH}
          transform={`translate(${board.xy[cell * 2]} ${
            board.xy[cell * 2 + 1]
          }) scale(0.9)`}
        />
      ))}
    </g>
  );
}

function Destination({
  at,
  capture,
  hovered,
}: {
  at: Point;
  capture: boolean;
  hovered: boolean;
}) {
  return (
    <g transform={`translate(${at.x} ${at.y})`} pointerEvents='none'>
      {capture
        ? (
          <path
            d={HEX_PATH}
            transform='scale(0.95)'
            className='fill-none stroke-accent'
            strokeWidth={hovered ? 0.14 : 0.1}
          />
        )
        : (
          <circle
            r={hovered ? 0.34 : 0.22}
            className='fill-accent'
            opacity={hovered ? 0.9 : 0.55}
          />
        )}
    </g>
  );
}

/**
 * One live bow: the limbs, the drawn string, and the arrow.
 *
 * The drawing is the rule (§4). The two flanking pieces are the bow's tips and the arc
 * runs forward through the piece between them, which is the grip. The string is drawn
 * back from the tips into the cell directly behind — and that is exactly the cell the
 * rule requires to be empty, so the picture explains the condition instead of merely
 * asserting it. The arrow is nocked there and flies through the piece to whatever the
 * ray can reach, because the piece *is* the arrow.
 */
function BowGlyph({
  board,
  shot,
  from,
  player,
  emphasised,
}: {
  board: BoardTopology;
  shot: BowShot;
  from: number;
  player: PlayerIndex;
  emphasised: boolean;
}) {
  if (from < 0 || shot.ray.length === 0) return null;

  const origin = centre(board, from);
  const a = centre(board, shot.flanks[0]);
  const b = centre(board, shot.flanks[1]);
  const behind = centre(board, shot.behind);
  const tip = centre(board, shot.ray[shot.ray.length - 1]);

  // Bulge the limbs forward — away from `behind` — by enough that the arc passes
  // through the grip. At 0.45 of the distance to the cell behind, the quadratic's
  // midpoint lands within a rounding error of the piece's own centre.
  const control = {
    x: origin.x + (origin.x - behind.x) * 0.45,
    y: origin.y + (origin.y - behind.y) * 0.45,
  };

  // Stop the arrow short of the destination's centre so its head sits on the edge of
  // the cell rather than covering whatever is standing there.
  const length = Math.hypot(tip.x - origin.x, tip.y - origin.y) || 1;
  const unit = {
    x: (tip.x - origin.x) / length,
    y: (tip.y - origin.y) / length,
  };
  const head = { x: tip.x - unit.x * 0.42, y: tip.y - unit.y * 0.42 };
  const normal = { x: -unit.y, y: unit.x };

  const stroke = player === 0 ? 'stroke-player-0' : 'stroke-player-1';
  const fill = player === 0 ? 'fill-player-0' : 'fill-player-1';
  // Legible unheld, emphatic under the pointer. This is the one thing on the board
  // that should draw the eye.
  const strength = emphasised ? 1 : 0.66;

  return (
    <g pointerEvents='none' opacity={strength}>
      {/* the bow */}
      <path
        d={`M ${a.x} ${a.y} Q ${control.x} ${control.y} ${b.x} ${b.y}`}
        className={`fill-none ${stroke}`}
        strokeWidth={emphasised ? 0.15 : 0.115}
        strokeLinecap='round'
      />
      {/* the string, drawn back into the cell the rule requires to be empty */}
      <path
        d={`M ${a.x} ${a.y} L ${behind.x} ${behind.y} L ${b.x} ${b.y}`}
        className={`fill-none ${stroke}`}
        strokeWidth={0.045}
        strokeLinejoin='round'
        opacity={0.8}
      />
      {/* the shot, nocked at the string and passing through the piece it launches */}
      <path
        d={`M ${behind.x} ${behind.y} L ${head.x} ${head.y}`}
        className={`fill-none ${stroke}`}
        strokeWidth={emphasised ? 0.11 : 0.08}
        strokeLinecap='round'
        strokeDasharray={emphasised ? undefined : '0.28 0.2'}
      />
      <path
        d={`M ${head.x + unit.x * 0.3} ${head.y + unit.y * 0.3}
            L ${head.x + normal.x * 0.15} ${head.y + normal.y * 0.15}
            L ${head.x - normal.x * 0.15} ${head.y - normal.y * 0.15} Z`}
        className={fill}
      />
    </g>
  );
}
